import { useNavigate } from "@solidjs/router";
import { BiSolidUserAccount } from "solid-icons/bi";
import {
  type Accessor,
  type Component,
  createEffect,
  createMemo,
  createSignal,
  Match,
  Show,
  Suspense,
  Switch,
  untrack,
} from "solid-js";
import { useI18n } from "@/api/i18n";
import { AvatarToDeprecate } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  FixedSheet,
  FixedSheetContent,
  FixedSheetFooter,
  FixedSheetHeader,
  ScrollableContent,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  SheetSaveButton,
  SheetSavePanel,
} from "@/domains/company/talent-pool/components/sheet-save-panel";
import { ProfileInfo } from "@/domains/freelance/profile/components/detail/profile-info";
import { ProfileInfoSkeleton } from "@/domains/freelance/profile/components/detail/profile-info-skeleton";
import { usePostHog } from "@/hooks/usePostHog";
import { useAutoConfidenceScoreExplanation, useSheetProfile } from "../api";
import { useCvSelection } from "../hooks/useCvSelection";
import {
  MAX_SELECTION,
  useFreelanceSearchStore,
} from "../store/freelance-search-store";
import type { FreelanceSheetData, SheetOrigin } from "../types";
import { toSelectedFreelancer } from "../utils/selection";
import { AnonymousCvButton } from "./AnonymousCvButton";
import { ConfidenceScoreBadge } from "./ConfidenceScoreBadge";
import { ConfidenceScoreExplanationBox } from "./ConfidenceScoreExplanationBox";

export type ExplanationStatus = "success" | "error" | "loading" | "none";

export const FreelanceSheetActions: Component<{
  freelancer: FreelanceSheetData;
  onClose?: () => void;
  // Pool tab (D9/OQ4): a non-discoverable pool card opens the sheet read-only —
  // the profile is viewable but contacting is disabled with a hint.
  disableContact?: boolean;
  /** Stato del box del commento al momento dell'azione (AC23). */
  explanationStatus?: ExplanationStatus;
}> = (props) => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { capture } = usePostHog();
  const [store, setStore] = useFreelanceSearchStore();
  const [dialogOpen, setDialogOpen] = createSignal(false);
  const cvSelection = useCvSelection();

  /* Speculare a `blockedByContact` in `AnonymousCvButton`: una selezione CV
     aperta chiude "Contatta". Le due selezioni non convivono — vedi il commento
     là per il perché. */
  const blockedByCv = () => cvSelection.count() > 0;

  const isSelected = () =>
    store.selectedFreelancers.some(
      (f) => String(f.id) === String(props.freelancer.id),
    );

  const addToStore = () => {
    const idStr = String(props.freelancer.id);
    setStore("selectedFreelancers", (current) => {
      if (current.some((f) => String(f.id) === idStr)) return current;
      if (current.length >= MAX_SELECTION) return current;
      return [...current, toSelectedFreelancer(props.freelancer)];
    });
  };

  const navigateToProposal = () => {
    if (store.jobOfferId) {
      navigate("/profile/company/search/freelancers/send-proposal");
    } else {
      navigate("/profile/company/search/freelancers/new-proposal");
    }
  };

  const handleContact = () => {
    if (blockedByCv()) return;
    capture("click_contact_button", {
      freelancer_id: props.freelancer.id,
      // AC23: il commento ha aiutato a decidere? Stato del box al click.
      explanation_status: props.explanationStatus ?? "none",
    });

    if (store.selectedFreelancers.length === 0) {
      addToStore();
      setDialogOpen(true);
    } else {
      addToStore();
      props.onClose?.();
    }
  };

  const handleRemoveFromSelection = () => {
    const idStr = String(props.freelancer.id);
    setStore("selectedFreelancers", (current) =>
      current.filter((f) => String(f.id) !== idStr),
    );
    props.onClose?.();
  };

  const handleSendProposal = () => {
    setDialogOpen(false);
    navigateToProposal();
  };

  const handleSelectMore = () => {
    setDialogOpen(false);
    props.onClose?.();
  };

  return (
    <div class="w-full min-h-20 lg:min-h-0 flex flex-col gap-2">
      <Show
        when={!props.disableContact}
        fallback={
          <div class="flex flex-col gap-1">
            <Button size="lg" class="w-full" disabled>
              <span class="text-sm">
                {t("company.talent_pool.match.unavailable_hint")}
              </span>
            </Button>
          </div>
        }
      >
        <Show
          when={!props.freelancer.already_applied}
          fallback={
            <Button size="lg" class="w-full" disabled>
              <span class="text-sm">{t("common.already_applied")}</span>
            </Button>
          }
        >
          <Show
            when={isSelected()}
            fallback={
              <Tooltip>
                {/* Come per la CTA gemella: il trigger è un `span`, perché un
                    bottone disabilitato non emette eventi di puntatore. */}
                <TooltipTrigger as="span" class="block w-full">
                  <Button
                    size="lg"
                    class="w-full"
                    onClick={handleContact}
                    disabled={
                      blockedByCv() ||
                      store.selectedFreelancers.length >= MAX_SELECTION
                    }
                  >
                    <span class="text-sm">{t("common.contact")}</span>
                  </Button>
                </TooltipTrigger>
                <Show when={blockedByCv()}>
                  <TooltipContent class="max-w-xs">
                    {t("common.contact_blocked_by_cv")}
                  </TooltipContent>
                </Show>
              </Tooltip>
            }
          >
            <Button
              size="lg"
              variant="destructiveOutline"
              class="w-full"
              onClick={handleRemoveFromSelection}
            >
              <span class="text-sm">{t("common.remove_from_selection")}</span>
            </Button>
          </Show>
        </Show>
      </Show>

      <AnonymousCvButton
        freelancer={props.freelancer}
        onClose={props.onClose}
      />

      <Dialog open={dialogOpen()} onOpenChange={setDialogOpen}>
        <DialogContent class="max-w-md">
          <DialogHeader>
            <div class="flex items-center gap-3 mb-3">
              <AvatarToDeprecate
                src={props.freelancer.avatar_url ?? ""}
                alt={props.freelancer.full_name ?? ""}
                class="h-10 w-10 shrink-0"
                fallback={<BiSolidUserAccount class="w-5 h-5" />}
              />
              <div class="min-w-0">
                <p class="font-semibold text-sm truncate">
                  {props.freelancer.full_name}
                </p>
                <Show when={props.freelancer.job_title}>
                  <p class="text-xs text-muted-foreground line-clamp-2 break-words">
                    {props.freelancer.job_title}
                  </p>
                </Show>
              </div>
            </div>
            <DialogTitle>
              {t("company.search.freelancers.result.proceed_dialog.title")}
            </DialogTitle>
            <DialogDescription>
              {t(
                "company.search.freelancers.result.proceed_dialog.description",
                { name: props.freelancer.full_name ?? "" },
              )}
            </DialogDescription>
          </DialogHeader>
          <div class="grid grid-cols-1 gap-3 mt-2">
            <button
              type="button"
              class="flex flex-col items-start gap-1 p-4 rounded-lg border border-gray-200 hover:border-primary hover:bg-primary/5 transition-colors text-left"
              onClick={handleSendProposal}
            >
              <span class="font-medium text-sm">
                {t(
                  "company.search.freelancers.result.proceed_dialog.send_proposal",
                )}
              </span>
              <span class="text-xs text-muted-foreground">
                {t(
                  "company.search.freelancers.result.proceed_dialog.send_proposal_desc",
                )}
              </span>
            </button>
            <button
              type="button"
              class="flex flex-col items-start gap-1 p-4 rounded-lg border border-gray-200 hover:border-primary hover:bg-primary/5 transition-colors text-left"
              onClick={handleSelectMore}
            >
              <span class="font-medium text-sm">
                {t(
                  "company.search.freelancers.result.proceed_dialog.select_more",
                )}
              </span>
              <span class="text-xs text-muted-foreground">
                {t(
                  "company.search.freelancers.result.proceed_dialog.select_more_desc",
                )}
              </span>
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export const FreelanceSheet: Component<{
  freelancer: FreelanceSheetData;
  open: Accessor<boolean>;
  onOpenChange: (open: boolean) => void;
  // Threaded to the footer actions — true for a non-discoverable pool card (D9).
  disableContact?: boolean;
  /**
   * Da quale tab arriva questa apertura. **Obbligatoria**: sceglie l'endpoint
   * del profilo, e solo il ramo `search` registra una `profile_view` lato BE —
   * un default sbaglierebbe l'endpoint (404 sui profili pool non-discoverable) e
   * inventerebbe un esito di ricerca. È anche la property `source` del funnel
   * del commento (AC23).
   */
  origin: SheetOrigin;
}> = (props) => {
  const { t } = useI18n();
  const { capture } = usePostHog();

  // Shared state for the two talent-pool slots (header button + body panel).
  // The button toggles `panelOpen`; the panel forwards `SaveFields`' commit up
  // via `registerCommit` so a dirty note is flushed on every dismissal path —
  // "Fatto", toggling "Salvato" off, and closing the sheet itself (D8).
  const freelancerId = createMemo(() => String(props.freelancer.id));
  const [panelOpen, setPanelOpen] = createSignal(false);
  const [store] = useFreelanceSearchStore();
  let commitNote: (() => void) | undefined;

  // Parity with the pool's `PoolProfileSheet`: the lean search-result row omits
  // portfolio, website, LinkedIn and bio, so we fetch the full public profile on
  // open and render `ProfileInfo` from it. The id is passed as an accessor, so
  // switching freelancer refetches without remounting — the route keeps the
  // selection while the sheet plays its exit animation. Re-opening the same
  // freelancer is served from cache.
  // See brain/tech-debt/company/active-search-profile-fetch.md.
  // Aperta dalla ricerca, la GET porta il `search_id` della pagina che ha
  // consegnato questa card (più il `job_offer_id` quando la ricerca è legata a
  // un'offerta): è così che il BE registra la `profile_view` come esito di
  // quella impression. Dal pool l'oggetto non porta `search_id` e il ramo resta
  // sul vecchio endpoint.
  const profileQuery = useSheetProfile(() => ({
    origin: props.origin,
    freelancerId: freelancerId(),
    params: {
      searchId: props.freelancer.search_id,
      jobOfferId: store.jobOfferId ?? undefined,
    },
    active: props.open(),
  }));

  /* AC23 — stato del commento per le properties di contatta/chiudi. Secondo
     observer sulla STESSA query del box (stessa key per breakdown, dedup:
     nessuna POST aggiuntiva). */
  const explanationQuery = useAutoConfidenceScoreExplanation(
    () => props.freelancer.confidence_score_breakdown,
    () => props.open(),
  );
  const explanationStatus = createMemo<ExplanationStatus>(() => {
    if (!props.freelancer.confidence_score_breakdown) return "none";
    if (explanationQuery.isSuccess) return "success";
    if (explanationQuery.isError) return "error";
    return "loading";
  });

  /* AC23 — funnel del commento sui fronti di `fetchStatus`. Vive qui e non nel
     box perché il box monta col profilo risolto (slot di `ProfileInfo`): una
     generazione conclusa prima del profilo non avrebbe alcun fronte da
     osservare. Questo observer invece è attivo da quando lo sheet si apre.
     Accumulatore (al primo run il fetch può essere già in volo). Una cache hit
     non fa partire nulla. Mai il testo del commento nelle properties.

     `paused` conta come VOLO APERTO, non come esito: con `networkMode: "online"`
     (default, mai sovrascritto) una rete assente non fa fallire la query, la
     mette in pausa. Chiudere il volo lì emetterebbe un `start_` orfano — né
     `end_` né `fail_` — e la ripresa ne emetterebbe un secondo per lo stesso
     tentativo. L'accumulatore è quindi «in volo?», non il `fetchStatus` grezzo:
     nessun evento sul fronte verso la pausa, nessun secondo `start_` al
     ritorno della rete. */
  createEffect<boolean>((wasInFlight) => {
    const fetchStatus = explanationQuery.fetchStatus;
    const inFlight = fetchStatus === "fetching" || fetchStatus === "paused";
    if (inFlight && !wasInFlight) {
      untrack(() =>
        capture("start_score_explanation", { source: props.origin }),
      );
    }
    if (!inFlight && wasInFlight) {
      untrack(() => {
        if (explanationQuery.isSuccess) {
          capture("end_score_explanation", { source: props.origin });
        } else if (explanationQuery.isError) {
          capture("fail_score_explanation", { source: props.origin });
        }
      });
    }
    return inFlight;
  }, false);

  const collapsePanel = () => {
    commitNote?.();
    setPanelOpen(false);
  };

  const handleSheetOpenChange = (open: boolean) => {
    // Sheet dismissal persists a dirty note first (D8), then resets the panel.
    if (!open) {
      commitNote?.();
      setPanelOpen(false);
      // AC23: la chiusura è l'"azione successiva" più frequente — con lo
      // stato del commento al momento dell'uscita.
      capture("close_freelance_sheet", {
        freelancer_id: props.freelancer.id,
        explanation_status: explanationStatus(),
      });
    }
    props.onOpenChange(open);
  };

  const focusSheetContent = (event: Event) => {
    event.preventDefault();
    (event.currentTarget as HTMLElement | null)?.focus({ preventScroll: true });
  };

  /* Box del commento (AC15/AC17). È una FUNZIONE che ritorna JSX, non un
     valore condiviso: i rami del `<Switch>` sotto sono mutuamente esclusivi,
     quindi ogni ramo istanzia il proprio box e non ne esistono mai due
     insieme. Keyed sul breakdown corrente (E-16): al cambio profilo si ricrea
     — mai il commento di A sotto il nome di B. Presente solo con breakdown
     (E-13: il gate della fetch vive nell'hook, qui non si replica). */
  const explanationBox = () => (
    <Show when={props.freelancer.confidence_score_breakdown} keyed>
      {(breakdown) => (
        <ConfidenceScoreExplanationBox
          breakdown={breakdown}
          active={props.open()}
        />
      )}
    </Show>
  );

  return (
    <FixedSheet open={props.open()} onOpenChange={handleSheetOpenChange}>
      <FixedSheetContent
        side="right"
        class="w-full md:max-w-2xl bg-card"
        onOpenAutoFocus={focusSheetContent}
      >
        <FixedSheetHeader>
          <SheetTitle class="text-sm font-medium">
            <div class="flex items-center gap-2 w-full justify-between pr-2 md:pr-4">
              <span class="text-sm font-medium">
                {t("profile.details.sheet_header.title")}
              </span>
              <div class="flex items-center gap-2">
                <Show when={props.freelancer.already_applied}>
                  <Badge variant="success" size="sm">
                    {t("common.already_applied")}
                  </Badge>
                </Show>
                {/* AC20: niente breakdown → niente tooltip ⓘ nello sheet
                    (resta sulla card); la spiegazione vive nel box sotto.
                    No linked offer ⇒ no confidence score to show (BE returns
                    null). */}
                <Show when={props.freelancer.confidence_score != null}>
                  <ConfidenceScoreBadge
                    score={props.freelancer.confidence_score!}
                  />
                </Show>
                <SheetSaveButton
                  freelancerProfileId={freelancerId()}
                  searchId={props.freelancer.search_id}
                  panelOpen={panelOpen}
                  onOpen={() => setPanelOpen(true)}
                  onCollapse={collapsePanel}
                />
              </div>
            </div>
          </SheetTitle>
        </FixedSheetHeader>
        <ScrollableContent>
          <SheetSavePanel
            freelancerProfileId={freelancerId()}
            panelOpen={panelOpen}
            onCollapse={collapsePanel}
            onRemoved={() => setPanelOpen(false)}
            registerCommit={(commit) => {
              commitNote = commit;
            }}
          />
          {/* Full profile — same rich view the pool sheet renders. Matching data
              (confidence_score) still comes from the search row, not the fetch. */}
          <Switch>
            <Match when={profileQuery.isPending}>
              {/* Scheletro, non spinner: la sheet sta ancora animando la
                  propria entrata, quindi il contenuto deve avere già la sua
                  geometria — un solo movimento sulla superficie. */}
              <ProfileInfoSkeleton />
            </Match>
            <Match when={profileQuery.isError}>
              {/* Profilo fallito: il commento — quando c'è — resta consegnato
                  sopra l'errore, così i due stati coesistono e i due retry
                  (commento e profilo) sono entrambi raggiungibili senza
                  scorrere. Ingombro contenuto: l'errore non spinge il box
                  fuori dalla prima schermata. */}
              <div class="flex min-h-[30vh] flex-col justify-center gap-4 py-4">
                {explanationBox()}
                <div class="flex flex-col items-center justify-center gap-3 text-center">
                  <p class="text-sm text-muted-foreground">
                    {t("company.search.freelancers.result.profile_error")}
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => void profileQuery.refetch()}
                  >
                    {t("common.retry")}
                  </Button>
                </div>
              </div>
            </Match>
            <Match when={profileQuery.data}>
              {(profile) => (
                /* Confine di sospensione DELLA SHEET (non un'ottimizzazione).
                   `ProfileInfo` monta le proprie query (esperienze, istruzione,
                   raccomandazioni) e solid-query sospende in prima fetch: senza
                   questo boundary la sospensione risale fino alla `<Suspense
                   fallback={<CompanyLayoutSkeleton />}>` della route in
                   `AppRouter`, che stacca dal DOM sidebar, header e lista dei
                   risultati e ci rimette lo scheletro d'app per la durata delle
                   tre richieste — la pagina sotto la sheet "lampeggia" e le card
                   sembrano ricaricarsi. Il fallback è lo stesso scheletro del
                   ramo pending, quindi la sheet resta ferma sulla sua geometria
                   fino al contenuto vero. */
                <Suspense fallback={<ProfileInfoSkeleton />}>
                  <ProfileInfo
                    profile={{
                      ...profile(),
                      confidence_score:
                        props.freelancer.confidence_score ?? undefined,
                    }}
                    /* Profilo risolto: il box resta nello slot di `ProfileInfo`,
                       ancorato subito sotto la barra del punteggio che spiega
                       (AC15). */
                    matchExplanation={explanationBox()}
                  />
                </Suspense>
              )}
            </Match>
          </Switch>
        </ScrollableContent>
        <FixedSheetFooter>
          <FreelanceSheetActions
            freelancer={props.freelancer}
            onClose={() => props.onOpenChange(false)}
            disableContact={props.disableContact}
            explanationStatus={explanationStatus()}
          />
        </FixedSheetFooter>
      </FixedSheetContent>
    </FixedSheet>
  );
};
